import { defineStore } from 'pinia';
import { ref } from 'vue';
import { RouteLocationNormalized, RouteLocationRaw } from 'vue-router';
import { PlatformType } from '@/types/common.types';
import { LINKED_VIEW_PATH_MAP } from '../define/linkedView.define';

export type LinkManagerParamsType = number | string | boolean | null | undefined;

export interface LinkManagerData {
  menuPath: string;
  params: Record<string, LinkManagerParamsType>;
  autoRetrieve: boolean;
}

const getSingleQueryValue = (value: unknown): string | undefined => {
  if (Array.isArray(value)) {
    const first = value[0];
    return first === null || first === undefined ? undefined : `${first}`;
  }
  if (value === null || value === undefined) {
    return undefined;
  }
  return `${value}`;
};

const parseLinkQueryValue = (value: string): LinkManagerParamsType => {
  const trimmed = value.trim();
  const lowered = trimmed.toLowerCase();
  if (lowered === 'true') {
    return true;
  }
  if (lowered === 'false') {
    return false;
  }

  // 숫자 형태만 number로 변환하고, 그 외 문자열은 원본(case 포함) 유지
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    const num = Number(trimmed);
    if (Number.isFinite(num)) {
      return num;
    }
  }

  return trimmed;
};

export const useLinkManagerStore = defineStore('linkManagerStore', () => {
  const linkToken = ref<string>('');

  const linkManagerData = ref<LinkManagerData>({
    menuPath: '',
    params: {},
    autoRetrieve: false,
  });

  const getLinkedViewPath = (toRoute?: RouteLocationNormalized): string => {
    const currentPath = toRoute?.path;

    const { platform = '', menuType = '' } = (toRoute?.query ?? {}) as {
      platform?: PlatformType;
      menuType?: string;
    };

    const linkedViewPath =
      platform && menuType ? LINKED_VIEW_PATH_MAP[platform]?.[menuType] : currentPath;

    return linkedViewPath ?? currentPath ?? '';
  };

  /**
   * 토큰 업데이트 후 linkToken 초기화
   */
  const resetLinkToken = () => {
    linkToken.value = '';
  };

  /**
   * linkToken과 linkManagerData를 초기화합니다.
   * 에러 발생 또는 유효하지 않은 linkToken 일 경우 호출됩니다.
   */
  const resetLinkManagerData = () => {
    linkManagerData.value = {
      menuPath: '',
      params: {},
      autoRetrieve: false,
    };
    resetLinkToken();
  };

  const initLinkManagerData = (toRoute?: RouteLocationNormalized) => {
    if (!toRoute || !toRoute.query.linkToken) {
      resetLinkManagerData();
      return;
    }

    const params: Record<string, LinkManagerParamsType> = {};
    Object.entries(toRoute.query).forEach(([key, value]) => {
      if (key === 'linkToken' || key === 'platform' || key === 'menuType') {
        return;
      }

      const singleValue = getSingleQueryValue(value);
      if (singleValue === undefined) {
        return;
      }

      params[key] = parseLinkQueryValue(singleValue);
    });

    if (typeof toRoute.query.linkToken === 'string') {
      linkToken.value = toRoute.query.linkToken;
    }

    linkManagerData.value = {
      menuPath: getLinkedViewPath(toRoute),
      params,
      autoRetrieve: true,
    };
  };

  const getLinkManagerRoute = (): RouteLocationRaw => {
    if (!linkManagerData.value.menuPath) {
      return '/logout';
    }

    return {
      path: linkManagerData.value.menuPath,
      replace: true,
    };
  };

  const isAutoRetrieve = (menuPath: string) => {
    return linkManagerData.value.menuPath === menuPath && linkManagerData.value.autoRetrieve;
  };

  /** 읽기 전용: 파라미터를 소비하지 않고 조회만 한다 */
  const peekParamsByMenuKey = (menuPath: string): Record<string, LinkManagerParamsType> | null => {
    if (isAutoRetrieve(menuPath)) {
      return linkManagerData.value.params;
    }
    return null;
  };

  /** 소비: 파라미터를 반환하고 초기화한다 */
  const consumeParamsByMenuKey = (
    menuPath: string,
  ): Record<string, LinkManagerParamsType> | null => {
    if (isAutoRetrieve(menuPath)) {
      const { params } = linkManagerData.value;
      resetLinkManagerData();
      return params;
    }
    return null;
  };

  /**
   * @deprecated peekParamsByMenuKey / consumeParamsByMenuKey 사용 권장.
   * 읽으면서 소비하는 destructive read — pre-apply 등에서 이중 호출 시 버그 유발.
   */
  const getParamsByMenuKey = (menuPath: string): Record<string, LinkManagerParamsType> | null => {
    return consumeParamsByMenuKey(menuPath);
  };

  return {
    linkToken,
    linkManagerData,
    initLinkManagerData,
    resetLinkManagerData,
    getLinkManagerRoute,
    isAutoRetrieve,
    peekParamsByMenuKey,
    consumeParamsByMenuKey,
    getParamsByMenuKey,
    resetLinkToken,
  };
});
